import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

const SearchableDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Search and select...',
  multiple = true,
  loading = false,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const searchLower = searchTerm.toLowerCase();
    const name = typeof option === 'string' ? option : (option.name || option.label || option.value || '');
    return name.toLowerCase().includes(searchLower);
  });

  // Handle option selection
  const handleSelect = (option) => {
    const optionValue = typeof option === 'string' ? option : (option.name || option.label || option.value);
    const optionId = typeof option === 'object' ? (option.id || option.value) : option;

    if (multiple) {
      if (Array.isArray(value)) {
        const isSelected = value.some(v => {
          const vId = typeof v === 'object' ? (v.id || v.value) : v;
          return vId === optionId;
        });

        if (isSelected) {
          // Remove if already selected
          onChange(value.filter(v => {
            const vId = typeof v === 'object' ? (v.id || v.value) : v;
            return vId !== optionId;
          }));
        } else {
          // Add if not selected
          onChange([...value, typeof option === 'object' ? option : optionValue]);
        }
      } else {
        onChange([typeof option === 'object' ? option : optionValue]);
      }
    } else {
      onChange(typeof option === 'object' ? option : optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Handle remove selected item
  const handleRemove = (item, e) => {
    e.stopPropagation();
    if (Array.isArray(value)) {
      const itemId = typeof item === 'object' ? (item.id || item.value) : item;
      onChange(value.filter(v => {
        const vId = typeof v === 'object' ? (v.id || v.value) : v;
        return vId !== itemId;
      }));
    }
  };

  // Get display value
  const getDisplayValue = (item) => {
    if (typeof item === 'string') return item;
    return item.name || item.label || item.value || '';
  };

  return (
    <div className="searchable-dropdown" ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      {/* Selected values display */}
      <div
        className="form-control"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          minHeight: '38px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px',
          padding: '6px 12px',
          backgroundColor: disabled ? '#f8f9fa' : 'white',
          border: '1px solid #ced4da',
          borderRadius: '4px'
        }}
      >
        {multiple && Array.isArray(value) && value.length > 0 ? (
          <>
            {value.map((item, index) => (
              <span
                key={index}
                className="badge"
                style={{
                  backgroundColor: '#e7f3ff',
                  color: '#0066cc',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {getDisplayValue(item)}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => handleRemove(item, e)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            ))}
          </>
        ) : !multiple && value ? (
          <span>{getDisplayValue(value)}</span>
        ) : (
          <span style={{ color: '#6c757d' }}>{placeholder}</span>
        )}
        <ChevronDown
          size={16}
          style={{
            marginLeft: 'auto',
            color: '#6c757d',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        />
      </div>

      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            marginTop: '4px',
            maxHeight: '300px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Search input */}
          <div style={{ padding: '8px', borderBottom: '1px solid #e9ecef' }}>
            <div style={{ position: 'relative' }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6c757d'
                }}
              />
              <input
                ref={inputRef}
                type="text"
                className="form-control"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  paddingLeft: '32px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px'
                }}
              />
            </div>
          </div>

          {/* Options list */}
          <div style={{ overflowY: 'auto', maxHeight: '250px' }}>
            {loading ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>
                Loading...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#6c757d' }}>
                No options found
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = typeof option === 'string' ? option : (option.name || option.label || option.value);
                const optionId = typeof option === 'object' ? (option.id || option.value) : option;
                const isSelected = Array.isArray(value) && value.some(v => {
                  const vId = typeof v === 'object' ? (v.id || v.value) : v;
                  return vId === optionId;
                });

                return (
                  <div
                    key={index}
                    onClick={() => handleSelect(option)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e7f3ff' : 'white',
                      borderBottom: '1px solid #f1f3f5',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{optionValue}</span>
                      {isSelected && (
                        <span style={{ color: '#0066cc', fontSize: '12px' }}>✓</span>
                      )}
                    </div>
                    {typeof option === 'object' && option.standards && option.standards.length > 0 && (
                      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>
                        Standards: {option.standards.join(', ')}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
